import { useStore } from 'hooks/useStore';
import {
  bulkUploadDataValidationInterfaceInstance,
  bulkUploadTmsResponseInterface,
  errorDescriptionInterface,
} from 'Interfaces/FactsInterfaces/tmsInterfaces';
import { Nullable } from 'primereact/ts-helpers';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { factScreenServices } from 'services/factsScreenServices';
import {
  getNextMonthFirstDay,
  DateConversionUtility,
} from 'utils/dateUtilities';
import { deepCopy } from 'utils/deepCopy';

interface optionsInterface {
  code: string;
  name: string;
}

const useDataValidationComponent = ({
  data,
  successCallBack,
}: {
  data: bulkUploadDataValidationInterfaceInstance[];
  successCallBack: () => void;
}) => {
  const [bulkUpdateData, setBulkUpdateData] =
    useState<bulkUploadDataValidationInterfaceInstance[]>(data);
  const [errorDescriptions, setErrorDescriptions] = useState<errorDescriptionInterface[]>([]);
  const [view, setView] = useState<'view' | 'edit'>('view');

  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isAnimationRunning, setIsAnimationRunning] = useState(false);
  const [bulkUpdateResponse, setBulkUpdateResponse] =
    useState<bulkUploadTmsResponseInterface | null>(null);
  const [storedStrategiesRowIndex, setStoredStrategiesRowIndex] = useState<null | number>(null);

  const {
    FactsStore: { targetMarketStrategyCodeOptions },
  } = useStore();

  const cotOptions: optionsInterface[] = [
    {
      code: 'MTF',
      name: 'MTF',
    },
    {
      code: 'CMOP',
      name: 'CMOP',
    },
    {
      code: 'TMOP',
      name: 'TMOP',
    },
    {
      code: 'VA Facility',
      name: 'VA Facility',
    },
  ];

  const [allStrategyOptions] = useState(targetMarketStrategyCodeOptions());

  const strategyOptions: optionsInterface[] = [
    {
      code: 'Direct',
      name: 'Direct',
    },
    {
      code: 'Indirect',
      name: 'Indirect',
    },
  ];

  const getCotSelectedOptionForRow = (rowIndex: number) => {
    let selectedOption: optionsInterface | null = null;
    for (let i = 0; i < cotOptions.length; i++) {
      if (cotOptions[i].code === bulkUpdateData[rowIndex].COT) {
        selectedOption = cotOptions[i];
      }
    }
    return selectedOption;
  };

  const getSellingCategorySelectedOptionForRow = (rowIndex: number) => {
    let selectedOption: optionsInterface | null = null;
    for (let i = 0; i < strategyOptions.length; i++) {
      if (strategyOptions[i].code === bulkUpdateData[rowIndex].SellingCategory) {
        selectedOption = strategyOptions[i];
      }
    }
    return selectedOption;
  };

  const getTmsStrategySelectedOptionForRow = (rowIndex: number) => {
    let selectedOption: optionsInterface | null = null;
    for (let i = 0; i < allStrategyOptions.length; i++) {
      if (allStrategyOptions[i].name === bulkUpdateData[rowIndex].TMSStrategy) {
        selectedOption = allStrategyOptions[i];
      }
    }
    return selectedOption;
  };

  const handleVerify = async () => {
    try {
      setIsBulkUpdating(true);
      const bulkUpdateResponse = await factScreenServices.bulkUpdateTms(bulkUpdateData);
      setBulkUpdateResponse(bulkUpdateResponse);
      if (bulkUpdateResponse === 'Success') {
        toast.success('Successfully Bulk Uploaded TMS');
        successCallBack();
        setErrorDescriptions([]);
        // toggle
        setView('view');
        return;
      }
      if (
        bulkUpdateResponse.isfileStructureCOTSellingCategoryTmsStrategyValidity.invalid ||
        bulkUpdateResponse.isFileDatesOverlapping ||
        bulkUpdateResponse.isStoredStrategiesDatesOverlapping
      ) {
        toast.error('Data validation failed, please resolve errors');
        setIsAnimationRunning(true);
        if (bulkUpdateResponse.isfileStructureCOTSellingCategoryTmsStrategyValidity.invalid) {
          setErrorDescriptions(
            bulkUpdateResponse.isfileStructureCOTSellingCategoryTmsStrategyValidity
              .error_descriptions
          );
          setTimeout(() => {
            setIsAnimationRunning(false);
          }, 12000);
        } else if (bulkUpdateResponse.isFileDatesOverlapping.invalid) {
          setErrorDescriptions(bulkUpdateResponse.isFileDatesOverlapping.error_descriptions);
          setTimeout(() => {
            setIsAnimationRunning(false);
          }, 13900);
        } else if (bulkUpdateResponse.isStoredStrategiesDatesOverlapping.invalid) {
          setErrorDescriptions(
            bulkUpdateResponse.isStoredStrategiesDatesOverlapping.error_descriptions
          );
          setTimeout(() => {
            setIsAnimationRunning(false);
          }, 12000);
        }
        setView('edit');
        setTimeout(() => {
          setIsAnimationRunning(false);
        }, 10000);
      } else {
        toast.success('Uploaded Successfully!');
        successCallBack();
      }
    } catch {
      toast.error('Failed to bulk update');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const checkIfThereAreErrors = () => {
    return errorDescriptions.length > 0;
  };

  function isNumeric(value: string) {
    return /^-?\d+$/.test(value);
  }

  const formUpdateMethods = {
    handleUpdateGFC: (gfc: string, rowIndex: number) => {
      if (isNumeric(gfc)) {
        const tempBulkUpdateData = deepCopy(bulkUpdateData);
        tempBulkUpdateData[rowIndex].GFC = Number(gfc);
        setBulkUpdateData(tempBulkUpdateData);
        return;
      }
    },

    handleUpdateTms: (tms: string, rowIndex: number) => {
      // resetError();

      // if (tms == "") {
      //   const tempBulkUpdateData = deepCopy(bulkUpdateData);
      //   tempBulkUpdateData[rowIndex].TargetMarketShare = 0;
      //   setBulkUpdateData(tempBulkUpdateData);
      // }
      // if (!isNumeric(tms)) {
      //   return;
      // }
      // if (tms != "" && !isNumeric(tms)) {
      //   const tempBulkUpdateData = deepCopy(bulkUpdateData);
      //   tempBulkUpdateData[rowIndex].TargetMarketShare = Number(tms);
      //   setBulkUpdateData(tempBulkUpdateData);
      //   return;
      // }
      // if (tms.indexOf(".") !== -1) {
      //   const tempBulkUpdateData = deepCopy(bulkUpdateData);
      //   tempBulkUpdateData[rowIndex].TargetMarketShare = Number(tms);
      //   setBulkUpdateData(tempBulkUpdateData);
      //   return;
      // }
      // if ((parseFloat(tms) >= 0 && parseFloat(tms) <= 1) || tms === "") {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].TargetMarketShare = tms;
      setBulkUpdateData(tempBulkUpdateData);
      return;
      // }
    },

    handleUpdateCOT: (cot: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].COT = cot;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateStartDate: (startDate: Date, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].StartDate = DateConversionUtility.convertLocalToDBDateString(
        getFirstDayOfMonth(startDate.toDateString())
      );
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateEndDate: (endDate: Date, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].EndDate = DateConversionUtility.convertLocalToDBDateString(
        getLastDayOfMonth(endDate.toDateString())
      );
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateSEllingCategory: (cateoory: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].SellingCategory = cateoory;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateTmsStrategy: (TmsStrategy: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].TMSStrategy = TmsStrategy;
      setBulkUpdateData(tempBulkUpdateData);
    },
  };

  const maxDateFromGivenAndTodays = (givenDate: Date) => {
    const date = new Date();
    if (givenDate > date) {
      return givenDate;
    } else {
      return date;
    }
  };

  const inputOperations = {
    gfc: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].GFC;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateGFC(value, rowIndex);
      },
    },
    cot: {
      value: (rowIndex: number) => {
        return getCotSelectedOptionForRow(rowIndex);
      },
      onChange: (rowIndex: number, value: string) => {
        console.log(value);
        formUpdateMethods.handleUpdateCOT(value, rowIndex);
      },
    },
    tms: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].TargetMarketShare;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateTms(value, rowIndex);
      },
    },
    sellingCategory: {
      value: (rowIndex: number) => {
        return getSellingCategorySelectedOptionForRow(rowIndex);
      },
      onChange: (rowIndex: number, value: string) => {
        console.log({ value });
        formUpdateMethods.handleUpdateSEllingCategory(value, rowIndex);
      },
    },
    startDate: {
      minDate: () => {
        return getNextMonthFirstDay(new Date().toDateString());
      },
      value: (rowIndex: number) => {
        return DateConversionUtility.convertDBDateToLocal(bulkUpdateData[rowIndex].StartDate);
      },
      onChange: (value: Nullable<Date>, rowIndex: number) => {
        formUpdateMethods.handleUpdateStartDate(value as Date, rowIndex);
      },
    },
    endDate: {
      minDate: (rowIndex: number) => {
        return maxDateFromGivenAndTodays(
          DateConversionUtility.convertDBDateToLocal(bulkUpdateData[rowIndex].StartDate)!
        );
      },
      value: (rowIndex: number) => {
        return DateConversionUtility.convertDBDateToLocal(bulkUpdateData[rowIndex].EndDate);
      },
      onChange: (value: Nullable<Date>, rowIndex: number) => {
        formUpdateMethods.handleUpdateEndDate(value as Date, rowIndex);
      },
    },
    tmsStrategy: {
      value: (rowIndex: number) => {
        return getTmsStrategySelectedOptionForRow(rowIndex);
      },
      onChange: (rowIndex: number, value: string) => {
        console.log(value);
        formUpdateMethods.handleUpdateTmsStrategy(value, rowIndex);
      },
    },
  };

  const handleRowDeleteButtonClick = (rowIndex: number) => {
    // delete the row from the bulkUpdateData
    if (rowIndex < bulkUpdateData.length) {
      const tempBulkUdateData = deepCopy(bulkUpdateData);
      tempBulkUdateData.splice(rowIndex, 1);
      setBulkUpdateData(tempBulkUdateData);
    }

    // fix the entries in the error description array

    const tempNewArrayDescription: errorDescriptionInterface[] = [];

    for (let i = 0; i < errorDescriptions.length; i++) {
      if (errorDescriptions[i].row < rowIndex) {
        tempNewArrayDescription.push(errorDescriptions[i]);
      } else if (errorDescriptions[i].row > rowIndex) {
        const editedErrorDescription = deepCopy(errorDescriptions[i]);
        editedErrorDescription.row = editedErrorDescription.row - 1;
        tempNewArrayDescription.push(editedErrorDescription);
      }
    }

    setErrorDescriptions(tempNewArrayDescription);
  };

  const getErrorDescription = (rowIndex: number) => {
    for (let i = 0; i < errorDescriptions.length; i++) {
      if (errorDescriptions[i].row === rowIndex) {
        return errorDescriptions[i].Error;
      }
    }
    return '';
  };

  const getMaxWidthForProgressBar = () => {
    if (bulkUpdateResponse && bulkUpdateResponse != 'Success') {
      if (bulkUpdateResponse.isfileStructureCOTSellingCategoryTmsStrategyValidity.invalid) {
        return '30% !important';
      } else if (bulkUpdateResponse.isFileDatesOverlapping.invalid) {
        return '55% !important';
      } else if (bulkUpdateResponse.isStoredStrategiesDatesOverlapping.invalid) {
        return '90% !important';
      }
    }
    return '100% !important';
  };

  const checkIfVerifyDataButtonIsDiabled = () => {
    return bulkUpdateData.length ? false : true;
  };

  const handleCloseStoredStrategiesModal = () => {
    setStoredStrategiesRowIndex(null);
  };

  const handleClickStoredStrategies = (rowIndex: number) => {
    setStoredStrategiesRowIndex(rowIndex);
  };

  return {
    bulkUpdateData,
    view,
    isBulkUpdating,
    checkIfThereAreErrors,
    errorDescriptions,
    handleVerify,
    handleRowDeleteButtonClick,
    getErrorDescription,
    isAnimationRunning,
    bulkUpdateResponse,
    getMaxWidthForProgressBar,
    cotOptions,
    strategyOptions,
    getCotSelectedOptionForRow,
    getSellingCategorySelectedOptionForRow,
    allStrategyOptions,
    getTmsStrategySelectedOptionForRow,
    inputOperations,
    checkIfVerifyDataButtonIsDiabled,
    storedStrategiesRowIndex,
    handleCloseStoredStrategiesModal,
    handleClickStoredStrategies,
  };
};

export default useDataValidationComponent;
